import { useMemo } from "react";
import {
  ActivityLineChartDataPoint,
  UseChartDataResult,
  buildActivityChartData,
  buildLegendValues,
} from "../../utils/chartDataUtils";
import { normalizeMetricDateRange } from "../../utils/metricDateRange";
import { ExternalMetricsParams } from "./externalMetricsTypes";
import { useAllAggregateExternalMetrics } from "./useAllAggregateExternalMetrics";

export { ActivityLineChartDataKey } from "../../utils/chartDataUtils";
export type {
  ActivityLineChartDataPoint,
  UseChartDataResult,
} from "../../utils/chartDataUtils";
export type { ExternalMetricsParams } from "./externalMetricsTypes";

export const useExternalChartData = (
  params: ExternalMetricsParams,
): UseChartDataResult => {
  const {
    startDate,
    endDate,
    dimension,
    licenseIds,
    minSessionDurationInMinutes,
  } = params;

  const normalizedParams = useMemo<ExternalMetricsParams>(() => {
    const normalizedRange = normalizeMetricDateRange(startDate, endDate);

    return {
      ...normalizedRange,
      dimension,
      licenseIds,
      minSessionDurationInMinutes,
    };
  }, [
    startDate,
    endDate,
    dimension,
    licenseIds,
    minSessionDurationInMinutes,
  ]);

  const { data, isLoading, isFetching } =
    useAllAggregateExternalMetrics(normalizedParams);

  const chartData = useMemo<ActivityLineChartDataPoint[]>(
    () =>
      buildActivityChartData(
        normalizedParams.startDate,
        normalizedParams.endDate,
        normalizedParams.dimension,
        data?.hoursInVr,
        data?.uniqueUsers,
        data?.userSessions,
      ),
    [
      data,
      normalizedParams.startDate,
      normalizedParams.endDate,
      normalizedParams.dimension,
    ],
  );

  const legendValues = useMemo(
    () =>
      buildLegendValues(data?.hoursInVr, data?.uniqueUsers, data?.userSessions),
    [data],
  );

  return {
    chartData,
    legendValues,
    isLoading,
    isFetching,
  };
};
